import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/authContext';
import { Input } from "../components/ui";
import { optionsPartido } from '../datos/options.js';

const ESTADOS = [
    { key: '', label: 'Todos', countKey: 'total' },
    { key: 'pendiente', label: 'Pendientes', countKey: 'pendiente' },
    { key: 'importado', label: 'Integrados a RED', countKey: 'importado' },
    { key: 'rechazado', label: 'Rechazados', countKey: 'rechazado' },
];

const estadoBadgeStyles = {
    pendiente: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    importado: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    rechazado: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const estadoLabels = {
    pendiente: 'Pendiente',
    importado: 'Integrado a RED',
    rechazado: 'Rechazado',
};

const PAGE_SIZE = 25;

export const AdminOsmPage = () => {
    const { token, role, userMunicipio } = useContext(AuthContext);
    const navigate = useNavigate();
    const esMunicipal = role === 'editor_municipio';
    const municipalPartidoLabel = optionsPartido.find((p) => p.value === userMunicipio)?.label || userMunicipio;

    const [registros, setRegistros] = useState([]);
    const [counts, setCounts] = useState({ pendiente: 0, importado: 0, rechazado: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);

    const [estado, setEstado] = useState('');
    const [partido, setPartido] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    const [actionId, setActionId] = useState(null);
    const [importTarget, setImportTarget] = useState(null);
    const [importNombre, setImportNombre] = useState('');
    const [importDomicilio, setImportDomicilio] = useState('');
    const [importError, setImportError] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [observaciones, setObservaciones] = useState('');
    const [rejectError, setRejectError] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const fetchRegistros = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
            if (estado) params.set('estado', estado);
            if (partido && !esMunicipal) params.set('codigoPartido', partido);
            if (search) params.set('search', search);

            const response = await fetch(`/api/entidades/osm-staging/admin?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al cargar registros OSM');

            setRegistros(data.registros || []);
            setCounts(data.counts || { pendiente: 0, importado: 0, rechazado: 0, total: 0 });
            setTotal(data.total || 0);
            setTotalPages(data.totalPages || 1);
        } catch (error) {
            setMessage(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRegistros();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estado, partido, search, page, token]);

    const openImport = (registro) => {
        setImportTarget(registro);
        setImportNombre(registro.nombre_completo || '');
        setImportDomicilio(registro.domicilio || '');
        setImportError(null);
    };

    const closeImport = () => {
        setImportTarget(null);
        setImportNombre('');
        setImportDomicilio('');
        setImportError(null);
    };

    const confirmImport = async () => {
        if (!importTarget) return;

        setActionId(importTarget.id);
        setImportError(null);
        setMessage(null);
        try {
            const response = await fetch(`/api/entidades/osm-staging/${importTarget.id}/import`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    nombre_completo: importNombre.trim(),
                    domicilio: importDomicilio.trim()
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al importar la entidad');

            setMessage(`Entidad importada correctamente (ID ${data.entidad_id_red})`);
            closeImport();
            fetchRegistros();
        } catch (error) {
            setImportError(error.message);
        } finally {
            setActionId(null);
        }
    };

    const openReject = (registro) => {
        setRejectTarget(registro);
        setObservaciones('');
        setRejectError(null);
    };

    const closeReject = () => {
        setRejectTarget(null);
        setObservaciones('');
        setRejectError(null);
    };

    const confirmReject = async () => {
        if (!rejectTarget) return;
        if (!observaciones.trim()) {
            setRejectError('Indicá el motivo del rechazo.');
            return;
        }

        setActionId(rejectTarget.id);
        setRejectError(null);
        try {
            const response = await fetch(`/api/entidades/osm-staging/${rejectTarget.id}/reject`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ observaciones: observaciones.trim() })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al rechazar la entidad');

            setMessage('Registro OSM rechazado');
            closeReject();
            fetchRegistros();
        } catch (error) {
            setRejectError(error.message);
        } finally {
            setActionId(null);
        }
    };

    const handleVerEnMapa = (registro) => {
        navigate('/mapa-privado', {
            state: {
                codigo_partido: registro.codigo_partido,
                evento_id: registro.id,
                osm_id: registro.osm_id,
                osm_lat: registro.lat,
                osm_lon: registro.lon,
                nombre_completo: registro.nombre_completo
            }
        });
    };

    const handleRestore = async (registro) => {
        if (!window.confirm(`¿Volver a pendiente "${registro.nombre_completo || registro.osm_id}"?`)) {
            return;
        }

        setActionId(registro.id);
        setMessage(null);
        try {
            const response = await fetch(`/api/entidades/osm-staging/${registro.id}/pending`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al volver a pendiente el registro');

            setMessage('Registro OSM vuelto a pendiente');
            fetchRegistros();
        } catch (error) {
            setMessage(error.message);
        } finally {
            setActionId(null);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-AR', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                            Validación OSM
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Registros importados desde OpenStreetMap para validar.
                        </p>
                    </div>
                    <div className="w-full sm:w-72">
                        <Input
                            type="text"
                            placeholder="Buscar por nombre, OSM ID o domicilio..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                    </div>
                </div>

                {message && (
                    <div className={`mb-4 rounded-md p-4 ${message.toLowerCase().includes('error') ? 'bg-red-50 dark:bg-red-900/30' : 'bg-green-50 dark:bg-green-900/30'}`}>
                        <p className={`text-sm font-medium ${message.toLowerCase().includes('error') ? 'text-red-800 dark:text-red-200' : 'text-green-800 dark:text-green-200'}`}>
                            {message}
                        </p>
                    </div>
                )}

                {/* Filtro por estado */}
                <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex space-x-8 overflow-x-auto">
                        {ESTADOS.map((e) => (
                            <button
                                key={e.key || 'todos'}
                                onClick={() => { setEstado(e.key); setPage(1); }}
                                className={`
                                    py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                                    ${estado === e.key
                                        ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'}
                                `}
                            >
                                {e.label}
                                <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                    {counts[e.countKey] || 0}
                                </span>
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Filtro por partido */}
                {esMunicipal ? (
                    <div className="mb-6 rounded-md bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 px-4 py-3 text-sm text-orange-800 dark:text-orange-200">
                        Mostrando registros del partido <span className="font-semibold">{municipalPartidoLabel}</span> ({userMunicipio})
                    </div>
                ) : (
                    <div className="mb-6 flex flex-col sm:flex-row gap-3">
                        <select
                            value={partido}
                            onChange={(e) => { setPartido(e.target.value); setPage(1); }}
                            className="w-full sm:w-72 text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                        >
                            <option value="">Todos los partidos</option>
                            {optionsPartido.map((p) => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Tabla */}
                <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <svg className="animate-spin h-8 w-8 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    ) : registros.length === 0 ? (
                        <p className="text-center text-gray-500 dark:text-gray-400 py-12">
                            No hay registros para mostrar.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Entidad</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Partido</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Resultado</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Fecha</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {registros.map((r) => (
                                        <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {r.nombre_completo || 'Sin nombre'}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {r.osm_type}/{r.osm_id?.split('/')[1] || r.osm_id}
                                                    {r.deportes ? ` · ${r.deportes}` : ''}
                                                </div>
                                                {r.domicilio && (
                                                    <div className="text-xs text-gray-400 dark:text-gray-500">{r.domicilio}</div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                                {r.partido_nombre || '-'}
                                                {r.codigo_partido && (
                                                    <span className="block text-xs text-gray-400 dark:text-gray-500">{r.codigo_partido}</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${estadoBadgeStyles[r.estado] || 'bg-gray-100 text-gray-800'}`}>
                                                    {estadoLabels[r.estado] || r.estado}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                                                {r.estado === 'importado' && r.entidad_id_red && (
                                                    <span className="font-medium text-gray-700 dark:text-gray-200">ID RED: {r.entidad_id_red}</span>
                                                )}
                                                {r.estado === 'rechazado' && (
                                                    <span className="italic">{r.observaciones || 'Sin observaciones'}</span>
                                                )}
                                                {r.estado === 'pendiente' && '-'}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                                {formatDate(r.imported_at || r.validated_at || r.created_at)}
                                            </td>
                                            <td className="px-6 py-4 text-right whitespace-nowrap">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleVerEnMapa(r)}
                                                        disabled={r.lat === null || r.lat === undefined || r.lon === null || r.lon === undefined}
                                                        title={r.lat === null || r.lat === undefined ? 'Sin coordenadas' : 'Ver en el mapa'}
                                                        className="px-3 py-1.5 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/60 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        Mapa
                                                    </button>
                                                    {r.estado === 'pendiente' ? (
                                                        <>
                                                            <button
                                                                onClick={() => openImport(r)}
                                                                disabled={actionId === r.id}
                                                                className="px-3 py-1.5 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 dark:bg-green-900/40 dark:text-green-300 dark:hover:bg-green-900/60 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {actionId === r.id ? '...' : 'Importar'}
                                                            </button>
                                                            <button
                                                                onClick={() => openReject(r)}
                                                                disabled={actionId === r.id}
                                                                className="px-3 py-1.5 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-900/60 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                Rechazar
                                                            </button>
                                                        </>
                                                    ) : r.estado === 'rechazado' ? (
                                                        <button
                                                            onClick={() => handleRestore(r)}
                                                            disabled={actionId === r.id}
                                                            className="px-3 py-1.5 text-sm font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            {actionId === r.id ? '...' : 'Volver a pendiente'}
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Paginación */}
                {!loading && total > 0 && (
                    <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <span>
                            {total} registro{total !== 1 ? 's' : ''} · página {page} de {totalPages}
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                                disabled={page <= 1}
                                className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                Anterior
                            </button>
                            <button
                                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                                disabled={page >= totalPages}
                                className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                Siguiente
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de importación con edición de nombre y domicilio */}
            {importTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-xl p-5">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Importar entidad OSM</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {importTarget.osm_id}
                                </p>
                            </div>
                            <button onClick={closeImport} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre del club</label>
                                <input
                                    type="text"
                                    value={importNombre}
                                    onChange={(e) => { setImportNombre(e.target.value); setImportError(null); }}
                                    placeholder="Nombre de la entidad"
                                    className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Domicilio</label>
                                <input
                                    type="text"
                                    value={importDomicilio}
                                    onChange={(e) => { setImportDomicilio(e.target.value); setImportError(null); }}
                                    placeholder="Domicilio"
                                    className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                />
                            </div>
                        </div>

                        {importError && (
                            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{importError}</p>
                        )}

                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                onClick={closeImport}
                                disabled={actionId === importTarget.id}
                                className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmImport}
                                disabled={actionId === importTarget.id}
                                className="px-4 py-2 rounded-md text-sm font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                            >
                                {actionId === importTarget.id ? 'Importando...' : 'Confirmar importación'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de rechazo con observaciones */}
            {rejectTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-xl p-5">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Rechazar entidad OSM</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {rejectTarget.nombre_completo || 'Sin nombre'} · {rejectTarget.osm_id}
                                </p>
                            </div>
                            <button onClick={closeReject} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
                        </div>

                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Motivo del rechazo <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={observaciones}
                            onChange={(e) => { setObservaciones(e.target.value); setRejectError(null); }}
                            rows={4}
                            placeholder="Indicá por qué se rechaza este registro..."
                            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />

                        {rejectError && (
                            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{rejectError}</p>
                        )}

                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                onClick={closeReject}
                                disabled={actionId === rejectTarget.id}
                                className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmReject}
                                disabled={actionId === rejectTarget.id}
                                className="px-4 py-2 rounded-md text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                            >
                                {actionId === rejectTarget.id ? 'Rechazando...' : 'Confirmar rechazo'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
